/**
 * Thin client bridge to the desktop shell's file-open commands (src-tauri/
 * src/lib.rs). The web app is served to the Tauri webview from the live site,
 * so this must feature-detect at runtime and cost nothing in browsers. Raw
 * `__TAURI_INTERNALS__.invoke` is the shell's established bridge (see the
 * theme sync in lib.rs) — no @tauri-apps npm dependency.
 *
 * Payloads cross the IPC as base64: the file caps here are document-sized
 * (≤50MB), where one decode pass is fine and keeps the commands plain
 * JSON-in/JSON-out.
 */

interface TauriInternals {
  invoke: (cmd: string, args?: Record<string, unknown>) => Promise<unknown>;
}

export function tauriInternals(): TauriInternals | null {
  if (typeof window === 'undefined') return null;
  const internals = (window as unknown as { __TAURI_INTERNALS__?: TauriInternals })
    .__TAURI_INTERNALS__;
  return internals && typeof internals.invoke === 'function' ? internals : null;
}

export function isDesktopShell(): boolean {
  return tauriInternals() !== null;
}

/**
 * Drain file paths queued by the OS (double-click / Open With) since the last
 * call. The Rust side buffers opens that arrive before the webview is ready
 * (cold launch) and between drains (second launches via single-instance).
 */
export async function takePendingOpens(): Promise<string[]> {
  const t = tauriInternals();
  if (!t) return [];
  try {
    const result = await t.invoke('os_take_pending_opens');
    return Array.isArray(result) ? result.filter((p): p is string => typeof p === 'string') : [];
  } catch {
    return [];
  }
}

export interface LocalFileRead {
  /** Stable per-device file identity (volume + inode / file index). */
  originId: string;
  name: string;
  bytes: ArrayBuffer;
}

export async function statAndRead(path: string): Promise<LocalFileRead | null> {
  const t = tauriInternals();
  if (!t) return null;
  try {
    const raw = (await t.invoke('os_stat_and_read', { path })) as {
      originId?: unknown;
      name?: unknown;
      base64?: unknown;
    };
    if (
      typeof raw?.originId !== 'string' ||
      typeof raw?.name !== 'string' ||
      typeof raw?.base64 !== 'string'
    ) {
      return null;
    }
    const bytes = base64ToBytes(raw.base64);
    return {
      originId: raw.originId,
      name: raw.name,
      // Uint8Array#buffer is typed ArrayBufferLike; this one is always a
      // plain ArrayBuffer (allocated right above).
      bytes: bytes.buffer as ArrayBuffer,
    };
  } catch {
    return null;
  }
}

/** Overwrite a local file with cloud bytes ("update the file on this computer"). */
export async function writeLocalFile(path: string, bytes: ArrayBuffer): Promise<boolean> {
  const t = tauriInternals();
  if (!t) return false;
  try {
    await t.invoke('os_write_file', { path, base64: bytesToBase64(new Uint8Array(bytes)) });
    return true;
  } catch {
    return false;
  }
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToBase64(bytes: Uint8Array): string {
  // btoa over chunks — a single fromCharCode call overflows the arg limit.
  let bin = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}
